import { Link } from "react-router-dom";

/**
 * CareSenta brand lockup: custom geometric mark + wordmark.
 *
 * The mark is a stylized "C enclosure" (healthcare = care / containment)
 * with a module node snapped inside its mouth (= the OS).
 * It should read as "the system that holds your care together".
 *
 * Deliberately not a heart, not a cross, not a plus — three shapes every
 * healthcare logo already uses.
 */
export default function Logo({ to = "/", inverse = false, size = "md", testId = "logo" }) {
  const sizes = {
    sm: { text: "text-lg",      mark: 22 },
    md: { text: "text-[22px]",  mark: 28 },
    lg: { text: "text-3xl",     mark: 40 },
  };
  const s = sizes[size] || sizes.md;
  const base = inverse ? "text-[#F8F7F4]" : "text-[#0A140F]";

  return (
    <Link
      to={to}
      data-testid={testId}
      aria-label="CareSenta home"
      className={`group inline-flex items-center gap-3 font-display ${s.text} leading-none tracking-tight ${base} select-none`}
    >
      <CareSentaMark size={s.mark} />

      <span className="flex items-baseline">
        <span className="font-medium">Care</span>
        <span className="font-bold text-[#8B2B3A] px-[0.1em]" aria-hidden>/</span>
        <span className="font-bold tracking-[-0.03em]">Senta</span>
        <span
          className={`ml-1 font-mono text-[8px] tracking-[0.25em] uppercase translate-y-[-0.7em] ${
            inverse ? "text-[#8A9992]" : "text-[#4A5A52]"
          }`}
          aria-hidden
        >
          ™
        </span>
      </span>
    </Link>
  );
}

/**
 * Standalone geometric glyph — shares logic with favicon.svg and og-cover.
 * 120×120 viewBox. Oxblood fill. Uses currentColor so callers can theme it.
 */
export function CareSentaMark({ size = 40, className = "" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      className={`shrink-0 text-[#8B2B3A] transition-transform duration-500 ease-out group-hover:rotate-90 ${className}`}
      aria-hidden
    >
      {/* C-bracket enclosure */}
      <path
        d="M 22 22 H 78 V 42 H 42 V 78 H 78 V 98 H 22 Z"
        fill="currentColor"
      />
      {/* Module node docked inside the opening */}
      <rect x="54" y="50" width="20" height="20" fill="currentColor" />
    </svg>
  );
}
